import { obtenerFotoPrincipal } from '../utils/categorias';
import { supabase } from './supabaseClient';
import { getNegocioId } from './negocioActual';

// Crea un pedido desde el catálogo (la clienta puede ser anónima).
export async function crearPedido({ productoId, codigo, nombre, precio, cantidad, clienteNombre, clienteTelefono, nota, origen }) {
  const { error } = await supabase.from('pedidos').insert([{
    negocio_id: getNegocioId(),
    producto_id: productoId || null,
    codigo_producto: codigo || null,
    nombre_producto: nombre || null,
    precio: parseFloat(precio) || 0,
    cantidad: parseInt(cantidad, 10) || 1,
    cliente_nombre: (clienteNombre || '').trim(),
    cliente_telefono: clienteTelefono?.trim() || null,
    nota: nota?.trim() || null,
    origen: origen || null, // de dónde llegó (sql/104)
  }]);
  if (error) throw error;
}

// Pedido del carrito (varias prendas). La BD toma nombre y precio de Kontrol.
export async function crearPedidoCarrito({ negocioId, items, clienteNombre, clienteTelefono, nota, origen, entrega, pago, direccion }) {
  const { data, error } = await supabase.rpc('crear_pedido_carrito', {
    p_negocio: negocioId || getNegocioId(),
    p_items: items.map((x) => ({ id: x.id, cantidad: x.cantidad })),
    p_nombre: (clienteNombre || '').trim(),
    p_telefono: (clienteTelefono || '').trim(),
    p_nota: nota?.trim() || null,
    p_origen: origen || null,
    p_entrega: entrega || null,
    p_pago: pago || null,
    p_direccion: direccion || null,
  });
  if (error) throw error;
  return data;
}

// Junta las prendas de un mismo pedido del carrito (mismo `grupo`) en uno solo.
// Los pedidos viejos (una prenda, sin grupo) quedan igual que antes.
export function agruparPedidos(filas) {
  const grupos = new Map(); const lista = [];
  (filas || []).forEach((p) => {
    if (!p.grupo) { lista.push({ ...p, ids: [p.id], prendas: [p], total: (p.precio || 0) * p.cantidad }); return; }
    let g = grupos.get(p.grupo);
    if (!g) { g = { ...p, ids: [], prendas: [], total: 0 }; grupos.set(p.grupo, g); lista.push(g); } // envio/entrega: iguales en todo el grupo
    g.ids.push(p.id); g.prendas.push(p); g.total += (p.precio || 0) * p.cantidad;
  });
  return lista;
}

// Pedidos del negocio (por defecto los pendientes). estado 'historial' = los ya atendidos.
export async function obtenerPedidos({ estado = 'pendiente' } = {}) {
  let query = supabase
    .from('pedidos')
    .select('*')
    .eq('negocio_id', getNegocioId())
    .order('creado_en', { ascending: false });
  if (estado === 'historial') query = query.neq('estado', 'pendiente');
  else if (estado) query = query.eq('estado', estado);
  const { data, error } = await query;
  if (error) throw error;
  const pedidos = data || [];
  // Foto principal de cada producto pedido (para verla y mandarla a la clienta).
  const ids = [...new Set(pedidos.map((p) => p.producto_id).filter(Boolean))];
  if (!ids.length) return pedidos;
  const { data: prods } = await supabase.from('productos')
    .select('id, foto_principal, foto_1_url, foto_2_url, foto_3_url, foto_4_url, foto_5_url')
    .eq('negocio_id', getNegocioId()).in('id', ids);
  const fotos = new Map((prods || []).map((x) => [x.id, obtenerFotoPrincipal(x)]));
  return pedidos.map((p) => ({ ...p, foto: fotos.get(p.producto_id) || null }));
}

// Pedidos (cualquier estado) desde una fecha, para conteos en Reportes.
export async function obtenerPedidosDesde(desde) {
  let query = supabase
    .from('pedidos')
    .select('id, estado, creado_en, grupo')
    .eq('negocio_id', getNegocioId())
    .order('creado_en', { ascending: false });
  if (desde) query = query.gte('creado_en', desde);
  const { data, error } = await query;
  if (error) throw error;
  return agruparPedidos(data); // un carrito cuenta como un pedido
}

// Cuenta pedidos (un carrito con varias prendas cuenta como uno).
export async function contarPedidosPendientes() {
  const { data, error } = await supabase
    .from('pedidos')
    .select('id, grupo')
    .eq('negocio_id', getNegocioId())
    .eq('estado', 'pendiente')
    .limit(1000);
  if (error) throw error;
  return agruparPedidos(data).length;
}

// id puede ser uno o varios (todas las prendas de un pedido del carrito).
const listaIds = (id) => (Array.isArray(id) ? id : [id]);

// Pedidos desde una fecha con todo lo necesario para las estadísticas de Pedidos.
export async function obtenerPedidosParaEstadisticas(desde = null) {
  const filas = [];
  for (let pagina = 0; pagina < 20; pagina += 1) { // la API entrega máx. 1000 por vez
    let q = supabase.from('pedidos').select('*').eq('negocio_id', getNegocioId())
      .order('creado_en', { ascending: false }).range(pagina * 1000, pagina * 1000 + 999);
    if (desde) q = q.gte('creado_en', desde);
    const { data, error } = await q;
    if (error) throw error;
    filas.push(...(data || []));
    if (!data || data.length < 1000) break;
  }
  return agruparPedidos(filas);
}

// Anota cómo terminó un pedido (sql/118): compro | seguimiento | no_compro.
// Lo pasa al Historial (estado 'atendido') y guarda cuándo se atendió.
export async function registrarResultadoPedido(id, { resultado, motivo = null, nota = null, atendidoEn = null }) {
  const cambios = {
    estado: 'atendido',
    resultado,
    motivo: resultado === 'no_compro' ? (motivo || 'otro') : null,
    nota_resultado: nota?.trim() ? nota.trim().slice(0, 300) : null,
  };
  if (atendidoEn) cambios.atendido_en = atendidoEn;
  const { error } = await supabase.from('pedidos').update(cambios).in('id', listaIds(id));
  if (!error) return;
  // Si todavía no se corrió sql/118, al menos se marca atendido como antes.
  if (/resultado|motivo|atendido_en|nota_resultado/.test(error.message || '')) {
    await actualizarEstadoPedido(id, 'atendido');
    const e = new Error('Se marcó atendido, pero falta correr sql/118 para guardar el resultado.');
    e.code = 'FALTA_SQL_118';
    throw e;
  }
  throw error;
}

// ✅ Confirmar pedido (sql/121): queda "confirmado" (la clienta lo ve en su
// enlace) con el envío y la forma de pago acordados. Sigue en Pendientes
// hasta que se registre la venta.
export async function confirmarPedido(id, { envio = undefined, pago = undefined } = {}) {
  const cambios = { etapa: 'confirmado' };
  if (envio !== undefined) cambios.envio = envio === '' || envio === null ? null : Number(envio);
  if (pago !== undefined) cambios.pago = pago || null;
  const { error } = await supabase.from('pedidos').update(cambios).in('id', listaIds(id));
  if (error) throw error;
}

// Etapa que ve la clienta: pagado → enviado/listo → entregado (la fecha la pone la base).
export async function cambiarEtapaPedido(id, etapa) {
  const { error } = await supabase.from('pedidos').update({ etapa }).in('id', listaIds(id));
  if (error) throw error;
}

// Cómo pagar por cada forma de pago (negocios.instrucciones_pago, sql/121). Solo la dueña lo ve.
export async function obtenerInstruccionesPago() {
  const { data, error } = await supabase.from('negocios').select('instrucciones_pago').eq('id', getNegocioId()).maybeSingle();
  if (error) throw error;
  return data?.instrucciones_pago || {};
}

export async function guardarInstruccionPago(metodo, texto) {
  const actuales = await obtenerInstruccionesPago();
  const nuevas = { ...actuales, [metodo]: String(texto || '').trim().slice(0, 600) };
  const { error } = await supabase.from('negocios').update({ instrucciones_pago: nuevas }).eq('id', getNegocioId());
  if (error) throw error;
}

// Apartado de las piezas (sql/123): extender N horas desde ahora o liberarlas ya.
export async function extenderApartado(id, horas = 24) {
  const hasta = new Date(Date.now() + horas * 3600000).toISOString();
  const { error } = await supabase.from('pedidos').update({ apartado_hasta: hasta }).in('id', listaIds(id));
  if (error) throw error;
  return hasta;
}

export async function liberarApartado(id) {
  const { error } = await supabase.from('pedidos').update({ apartado_hasta: new Date().toISOString() }).in('id', listaIds(id));
  if (error) throw error;
}

// Piezas apartadas ahora mismo, por producto: { [producto_id]: { cantidad, cliente, grupo, hasta } }.
export async function obtenerApartados() {
  const { data, error } = await supabase.from('pedidos')
    .select('producto_id, cantidad, cliente_nombre, grupo, apartado_hasta, etapa')
    .eq('negocio_id', getNegocioId()).eq('estado', 'pendiente').neq('etapa', 'cancelado')
    .not('producto_id', 'is', null);
  if (error) throw error;
  const ahora = Date.now();
  const mapa = {};
  (data || []).forEach((p) => {
    const vigente = (p.apartado_hasta && new Date(p.apartado_hasta).getTime() > ahora) || ['pagado', 'enviado', 'listo'].includes(p.etapa);
    if (!vigente) return;
    const a = mapa[p.producto_id] || { cantidad: 0, clientes: [], grupos: [], hasta: p.apartado_hasta };
    a.cantidad += Number(p.cantidad || 0);
    a.clientes.push(p.cliente_nombre);
    a.grupos.push(p.grupo);
    mapa[p.producto_id] = a;
  });
  return mapa;
}

export async function actualizarEstadoPedido(id, estado) {
  const { error } = await supabase.from('pedidos').update({ estado }).in('id', listaIds(id));
  if (error) throw error;
}

export async function eliminarPedido(id) {
  const { error } = await supabase.from('pedidos').delete().in('id', listaIds(id));
  if (error) throw error;
}
